import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  type Brand,
  useSurveyQueryActiveBrandGroup,
  useSurveyQueryData,
  useSurveyQueryQuestions,
  useSurveyQuerySurveyFilterTextGroups,
} from '~/hooks/useSurveyQuery'
import {
  assertExists,
  dataDisplayTypeLabelI18nKeyMap,
  exists,
  type FigureInfo,
  type FiveNumber,
  fiveSegmentLabelI18nKeyMap,
  formatNineSegment,
  type NineNumber,
  segmentDisplayTypeLabelI18nKeyMap,
  toFiveSegment,
} from '~/utils'

import {
  dataDisplayTypeAtom,
  lastTabulationParamsAtom,
  segmentDisplayTypeAtom,
} from '../atoms'
import { pathKey } from '../utils'

type UseFigureInfoOptions = {
  brand?: Brand
  fromSegmentNumber?: number
  toSegmentNumber?: number
}

export const useFigureInfo = ({
  brand,
  fromSegmentNumber,
  toSegmentNumber,
}: UseFigureInfoOptions = {}): FigureInfo => {
  const { t } = useTranslation()
  const survey = useSurveyQueryData()
  const brandGroup = useSurveyQueryActiveBrandGroup()
  const surveyFilterTextGroups = useSurveyQuerySurveyFilterTextGroups()

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)
  // 表示中の集計結果と設問テキストの不整合を避けるため、
  // 現在の選択値ではなく直近の集計時パラメータを参照する。
  const lastTabulationParams = useAtomValue(lastTabulationParamsAtom)
  const allQuestions = useSurveyQueryQuestions()

  return React.useMemo(() => {
    assertExists(survey, t('assertion_failed'))

    const getSegmentLabel = (segmentNumber: number): string =>
      segmentDisplayType === 'fiveSegs'
        ? t(
            fiveSegmentLabelI18nKeyMap[
              toFiveSegment(segmentNumber as FiveNumber)
            ],
          )
        : formatNineSegment(segmentNumber as NineNumber, '%n. %t1', t)

    const segmentRoute =
      exists(fromSegmentNumber) && exists(toSegmentNumber)
        ? `${getSegmentLabel(fromSegmentNumber)} → ${getSegmentLabel(toSegmentNumber)}`
        : undefined

    const findQuestionText = (
      questionId: string | null,
    ): string | undefined => {
      if (!exists(questionId)) {
        return undefined
      }
      const question = allQuestions.find((q) => q.question_id === questionId)

      return question?.question_text
    }

    return {
      brand: brand?.name,
      brandAccountId: survey.brandAccountId,
      brandGroup: brandGroup?.name,
      dataDisplayType: dataDisplayTypeLabelI18nKeyMap[dataDisplayType],
      importanceQuestion: findQuestionText(
        lastTabulationParams?.importanceQuestionId ?? null,
      ),
      liftSaQuestion: findQuestionText(
        lastTabulationParams?.liftSaQuestionId ?? null,
      ),
      npiDriverQuestion: findQuestionText(
        lastTabulationParams?.npiDriverQuestionId ?? null,
      ),
      pathKey,
      segmentDisplayType: segmentDisplayTypeLabelI18nKeyMap[segmentDisplayType],
      segmentRoute,
      survey: survey.title,
      surveyFilter: surveyFilterTextGroups,
    }
  }, [
    allQuestions,
    brand?.name,
    brandGroup?.name,
    dataDisplayType,
    fromSegmentNumber,
    lastTabulationParams,
    segmentDisplayType,
    survey,
    surveyFilterTextGroups,
    t,
    toSegmentNumber,
  ])
}
